"use client";

import { useEffect, useState } from "react";
import { Button, SubmitButton } from "@/components/ui/button";
import {
  Modal,
  ModalContent,
  ModalClose,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { StageSelect } from "@/features/jobs/components/stage-select";
import { useGetJob } from "@/lib/api/generated";

interface MoveToStageProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  onConfirm: (stageId: string) => void | Promise<void>;
  loading?: boolean;
  count: number;
}

export function MoveToStage({
  open,
  onOpenChange,
  jobId,
  onConfirm,
  loading,
  count,
}: MoveToStageProps) {
  const { data: job } = useGetJob(jobId, { query: { enabled: open } });
  const firstStageId =
    [...(job?.stages ?? [])].sort((a, b) => a.position - b.position)[0]?.id ??
    "";

  const [selectedStageId, setSelectedStageId] = useState<string>("");

  useEffect(() => {
    if (open && firstStageId) setSelectedStageId(firstStageId);
  }, [open, firstStageId]);

  const handleConfirm = () => {
    if (!selectedStageId) return;
    onConfirm(selectedStageId);
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent className="max-w-[528px]!">
        <ModalHeader
          title={`Move ${count} ${count === 1 ? "Profile" : "Profiles"} to Stage`}
        />

        <div className="p-6">
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-0.5">
              <label className="text-sm font-medium text-heading">
                Destination stage
              </label>
              <p className="text-sm text-muted-foreground leading-normal">
                {count === 1
                  ? "This profile moves to the stage you select below."
                  : "These profiles move to the stage you select below."}
              </p>
            </div>
            <StageSelect
              jobId={jobId}
              value={selectedStageId || null}
              onChange={setSelectedStageId}
              placeholder="Select a stage"
            />
          </div>
        </div>

        <ModalFooter>
          <ModalClose asChild>
            <Button variant="secondary" size="sm" className="h-9 sm:h-7">Cancel</Button>
          </ModalClose>
          <SubmitButton
            variant="primary"
            size="sm"
            className="h-9 sm:h-7"
            onClick={handleConfirm}
            disabled={!selectedStageId || loading}
            loading={loading}
            loadingLabel="Moving"
          >
            Move to Stage
          </SubmitButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
